import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { useFileShare } from '../../../lib/use-file-share';
import { accessLine, shareErrorKey, shareLabel } from '../../../lib/share-flow';
import { FILE_SHARE_COPY } from '../../../lib/file-share-flow';
import { Badge, Button, Card, TextButton, Typography } from '../ui';
import { RecipientField } from '../share/RecipientField';
import { GrantNotices } from '../share/GrantNotices';
import { mergeOutcomes, type PickedRecipient } from '../../../lib/recipient-field';
import type { GrantOutcome } from '../../../lib/share-flow';
import { useState } from 'react';
import styles from '../apps/ShareAppDialog.module.css';

// "Share" on a file: give someone access to read it, and see or undo who already has it.
//
// Named FileAccessDialog rather than ShareFileDialog because that name is taken by the OTHER thing
// called sharing on a file — `ShareFileDialog.tsx` copies a link or mails a copy, and grants nobody
// anything. That one is being renamed "Send a copy"; this is the one that changes who can read.
//
// The only interesting thing on screen is the line under the people list, and it has THREE states,
// not two — `lib/share-flow.ts` decides which, this file only renders it:
//
//   loaded + empty  → "Private" + the hint. We asked, and nobody has access.
//   loaded + people → "Shared with N people", above the rows themselves.
//   FAILED to load  → "Couldn't load who has access" + Try again. We do NOT know, and say so.
//
// A failed read shown as "Private" would be this dialog lying about a permission, which is why the
// error branch is first-class all the way down to the API route (see pages/api/file-shares.ts).
export function FileAccessDialog({
  fileId,
  filePath,
  fileName,
  onClose,
}: {
  /** `FileSystem.File.id`, or empty for a file written before that field existed. */
  fileId: string;
  /** The file's full `workspaces` key. Resolves the id server-side when there is none yet. */
  filePath: string;
  fileName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const flow = useFileShare(fileId, filePath);
  // The people the owner has picked but not yet shared with. Held here rather than in the share
  // machine because the machine is about ONE attempt — it is what turns an address into a grant, and
  // a list of intentions is not an attempt.
  const [picked, setPicked] = useState<PickedRecipient[]>([]);
  // Held for the WHOLE run, not per grant. `state.submitting` goes false between grants, so a
  // second press mid-run would launch a duplicate run over the same list.
  const [sending, setSending] = useState(false);
  const [outcomes, setOutcomes] = useState<GrantOutcome[]>([]);
  const { state, shares } = flow;
  const line = accessLine(shares, FILE_SHARE_COPY);

  // Everybody picked, in one press. What comes back is who did NOT land — they stay on screen, so
  // nobody has to remember which of five names went through — plus each landed grant's own notices,
  // which the single-attempt state can only hold one of.
  const share = async (confirmUnknown = false) => {
    if (picked.length === 0 || sending) return;
    setSending(true);
    try {
      const result = await flow.submitMany(picked.map((p) => p.identifier), confirmUnknown);
      setPicked((current) => current.filter((p) => result.failed.includes(p.identifier)));
      setOutcomes((previous) => mergeOutcomes(previous, result.outcomes));
    } finally {
      setSending(false);
    }
  };

  const busy = state.submitting || sending;

  // Editing the list retires the LAST attempt's verdict. A red "couldn't share this" describes a
  // press that is no longer what the screen says, and leaving it up while somebody fixes the list
  // makes it read as a problem with what they are fixing. Both phones already did this; web was
  // relying on `flow.setEmail`, which the field replaced. The landed grants' notices stay — those
  // are about shares that really happened.
  const editPicked = (next: PickedRecipient[]) => {
    setPicked(next);
    if (state.error) flow.clearError();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      title={t('files.access.title')}
      description={fileName}
      footer={
        state.confirmEmail ? (
          // The question REPLACES the Share button rather than sitting beside it: nothing was
          // written, and leaving the original action live invites the owner to press it again and
          // get asked again. Cancel is not destructive-styled — declining to share with a stranger
          // is an ordinary answer, not damage.
          <div className={styles.confirmActions}>
            <Button variant="outlined" disabled={busy} onClick={() => flow.cancelConfirm()}>
              {t('files.access.confirmUnknown.cancel')}
            </Button>
            {/* Answering resumes the whole list from where it stopped. The yes is honoured only while
                that list still BEGINS with the address the server asked about — `confirmAppliesTo` —
                so removing the chip under the question cannot transfer the answer to someone else. */}
            <Button loading={busy} disabled={picked.length === 0} onClick={() => void share(true)}>
              {t('files.access.confirmUnknown.confirm')}
            </Button>
          </div>
        ) : (
          <Button loading={busy} disabled={picked.length === 0} onClick={() => void share()}>
            {t('files.access.submit')}
          </Button>
        )
      }
    >
      <div className={styles.body}>
        <RecipientField
          // Empty is the only honest input while the read is out or has FAILED — we do not know who
          // holds a grant, and inventing an exclusion list would hide people who do. A duplicate that
          // slips through is a server-side no-op reported as `alreadyShared`, which is why this is the
          // cheap error and claiming knowledge is not.
          alreadyHasAccess={shares.status === 'ready' ? shares.shares.map((s) => s.granteeEmail ?? '').filter(Boolean) : []}
          picked={picked}
          onPickedChange={editPicked}
          disabled={busy}
          errorMessage={
            state.error === 'invalidEmail' || state.error === 'shareFailed'
              ? t(shareErrorKey(state.error, FILE_SHARE_COPY))
              : undefined
          }
          onSubmit={() => void share()}
          bi={{ pageName: 'workspace_files', modalName: 'file_access' }}
        />

        {/* Every landed grant's own notices — one block per person. The share machine holds only the
            LAST attempt's, and in a five-person run the notice worth reading is rarely the fifth. */}
        <GrantNotices
          outcomes={outcomes}
          copy={{ invited: 'files.access.invited', notifyFailed: 'files.access.notifyFailed', linkHint: 'files.access.linkHint' }}
        />

        {/* NOTHING HAS BEEN WRITTEN YET. Sharing by email is get-or-create, so a typo and a real
            stranger look identical afterwards — this is the one moment the difference can still be
            acted on, which is why it is a question and not the notice above. It matters more here
            than it does for an app: a mistyped app share hands somebody a thing that runs on their
            own data, and a mistyped file share hands them this document. */}
        {state.confirmEmail && (
          <Card variant="stone" size="sm" className={styles.confirm}>
            <Typography variant="p2" color="strong" component="p">
              {t('files.access.confirmUnknown.title', { email: state.confirmEmail })}
            </Typography>
            <Typography variant="p3" color="secondary" component="p">
              {t('files.access.confirmUnknown.body')}
            </Typography>
          </Card>
        )}

        <div className={styles.people}>
          <Typography variant="h4" color="strong" component="h3">
            {t('files.access.peopleTitle')}
          </Typography>

          {/* Nothing is claimed while the read is still out — `accessLine` answers null, no line. */}
          {line?.kind === 'private' && (
            <div className={styles.line}>
              <Typography variant="p2" color="secondary" component="p">
                {t(line.key)}
              </Typography>
              <Typography variant="p3" color="muted" component="p">
                {t(line.hintKey)}
              </Typography>
            </div>
          )}

          {line?.kind === 'loadFailed' && (
            <div className={styles.retryRow}>
              <Typography variant="p2" color="secondary" component="p">
                {t(line.key)}
              </Typography>
              <TextButton size="small" onClick={() => flow.retry()}>
                {t(line.retryKey)}
              </TextButton>
            </div>
          )}

          {line?.kind === 'shared' && (
            <Typography variant="p2" color="secondary" component="p">
              {line.params ? t(line.key, line.params) : t(line.key)}
            </Typography>
          )}

          {shares.status === 'ready' &&
            shares.shares.map((share) => {
              // A share may carry neither a name nor an email. It is still a grant, so it is still
              // listed and still revocable — the row is labelled rather than dropped.
              const label = shareLabel(share, FILE_SHARE_COPY);
              return (
                <div key={share.granteeRecipientId} className={styles.row}>
                  <Typography variant="p2" color="default" component="span" className={styles.rowLabel}>
                    {'text' in label ? label.text : t(label.key)}
                  </Typography>
                  {/* Invited and has-access are different facts, and only this says which. Absent
                      reads as accepted, so an older server renders as it does today rather than
                      marking every existing grant unanswered. */}
                  {share.accepted === false && (
                    <Badge variant="waiting-on-a-reply" label={t('files.access.invitedPending')} />
                  )}
                  {/* What this person can do, as a LABEL and never a control. There is one kind of
                      file access today — read — so a menu here would offer a choice that does not
                      exist, and a disabled one reads as broken rather than as deliberate. It is
                      named anyway so the model is on screen before Editor exists to join it.

                      AFTER the badge: the badge is an adjective on the NAME and belongs touching it,
                      which is the order both phones already draw. */}
                  <Typography variant="p3" color="muted" component="span" className={styles.rowMeta}>
                    {t('files.access.role.viewer')}
                  </Typography>
                  {/* "added by Sam" — only when it was NOT you. Rendering your own name on every row
                      you created would be noise on the common case. */}
                  {share.grantedByName && (
                    <Typography variant="p3" color="muted" component="span" className={styles.rowMeta}>
                      {t('files.access.addedBy', { name: share.grantedByName })}
                    </Typography>
                  )}
                  <TextButton
                    size="small"
                    color="destructive"
                    disabled={state.removing === share.granteeRecipientId}
                    // The id comes off the row that was rendered, so a revoke can only target the
                    // person whose Remove was pressed.
                    onClick={() => void flow.remove(share.granteeRecipientId)}
                  >
                    {t('files.access.remove')}
                  </TextButton>
                </div>
              );
            })}

          {state.error === 'removeFailed' && (
            <Typography variant="p3" color="secondary" component="p">
              {t(shareErrorKey(state.error, FILE_SHARE_COPY))}
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
}
